import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import toast, { Toaster } from 'react-hot-toast';
import {
  AlertCircle,
  ArrowLeft,
  Building2,
  CheckCircle2,
  ExternalLink,
  IndianRupee,
  Loader2,
  Truck,
  User,
  Wallet,
} from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import {
  fetchGrievanceById,
  resolveGrievance,
  retryGrievanceRefund,
  GrievanceDetailPayload,
  GrievanceListRow,
} from '../../services/MasterAdmin/grievanceAdminService';
import { GrievanceAttachmentChips } from '../../components/MasterAdmin/grievances/GrievanceAttachmentChips';
import { GrievanceServiceSnapshotPanel } from '../../components/MasterAdmin/grievances/GrievanceServiceSnapshotPanel';
import { GrievancePolicyLinks } from '../../components/MasterAdmin/grievances/GrievancePolicyLinks';

const formatInr = (n: number | string | null | undefined) => {
  const v = Number(n);
  if (!Number.isFinite(v)) return '—';
  return `₹${v.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
};

const round2 = (n: number) => Math.round(n * 100) / 100;

type ShareKey = 'vendor' | 'user' | 'vedika';

function parseSharePct(s: string): number | null {
  const t = s.trim();
  if (t === '') return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
}

/** Which share % to auto-fill when two of three are known (remainder bucket when all filled). */
function getAutoFillTarget(edited: ShareKey, state: Record<ShareKey, string>): ShareKey | null {
  const keys: ShareKey[] = ['vendor', 'user', 'vedika'];
  const empty = keys.filter((k) => k !== edited && parseSharePct(state[k]) == null);
  if (empty.length === 1) return empty[0];
  if (empty.length === 0) return 'vedika';
  return null;
}

function applySharePctChange(
  edited: ShareKey,
  raw: string,
  current: Record<ShareKey, string>,
): Record<ShareKey, string> {
  const next: Record<ShareKey, string> = { ...current, [edited]: raw };
  if (raw.trim() === '') return next;

  const editedVal = parseSharePct(raw);
  if (editedVal == null) return next;

  const target = getAutoFillTarget(edited, next);
  if (!target) return next;

  const keys: ShareKey[] = ['vendor', 'user', 'vedika'];
  let sumOthers = 0;
  for (const k of keys) {
    if (k === edited || k === target) continue;
    const v = parseSharePct(next[k]);
    if (v == null) return next;
    sumOthers += v;
  }

  next[target] = String(round2(100 - editedVal - sumOthers));
  return next;
}

function resolveErrorMessage(e: unknown): string {
  const data = (e as { response?: { data?: Record<string, unknown> } })?.response?.data;
  if (data) {
    const err = data.error ?? data.message;
    if (typeof err === 'string' && err.trim()) return err.trim();
  }
  if (e instanceof Error && e.message) return e.message;
  return 'Resolve failed';
}

const ledgerServiceType = (serviceType: string) => {
  if (serviceType === 'DOCTOR') return 'DOCTOR_INSTANT';
  return serviceType;
};

const maOrderLedgerPath = (serviceRefId: string, serviceType: string) => {
  const params = new URLSearchParams({
    serviceType: ledgerServiceType(serviceType),
    returnTo: 'grievances',
  });
  return `/master-admin/operations/order-ledger/${encodeURIComponent(serviceRefId)}?${params.toString()}`;
};

const userProfilePath = (userId: string) => `/users/${encodeURIComponent(userId)}`;

const ambulanceBookingPath = (vendorId: string, bookingId: string) =>
  `/vendors/ambulance/${encodeURIComponent(vendorId)}/booking/${encodeURIComponent(bookingId)}`;

const medicineOrderPath = (vendorId: string, orderId: string) =>
  `/vendors/medical-store/${encodeURIComponent(vendorId)}/order/${encodeURIComponent(orderId)}`;

type ShareAllocateCardProps = {
  label: string;
  sublabel: string;
  pct: string;
  onPctChange: (value: string) => void;
  amount: number;
  icon: React.ComponentType<{ className?: string }>;
  border: string;
  bg: string;
  accent: string;
  inputRing: string;
};

const ShareAllocateCard: React.FC<ShareAllocateCardProps> = ({
  label,
  sublabel,
  pct,
  onPctChange,
  amount,
  icon: Icon,
  border,
  bg,
  accent,
  inputRing,
}) => (
  <div className={`flex flex-col rounded-xl border p-4 sm:p-5 ${border} ${bg}`}>
    <div className="flex items-start gap-3">
      <div
        className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-white/80 shadow-sm dark:bg-gray-900/50 ${accent}`}
      >
        <Icon className="h-5 w-5" />
      </div>
      <div className="min-w-0 flex-1">
        <p className={`text-sm font-semibold ${accent}`}>{label}</p>
        <p className="text-xs text-gray-500 dark:text-gray-400">{sublabel}</p>
      </div>
    </div>

    <div className="mt-4 flex flex-col gap-4">
      <label className="block w-full">
        <span className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
          Share %
        </span>
        <div className="relative mt-1.5">
          <input
            type="number"
            min={0}
            max={100}
            step="0.01"
            inputMode="decimal"
            className={`w-full rounded-lg border border-gray-300 bg-white py-3 pl-3 pr-10 text-xl font-semibold tabular-nums text-gray-900 shadow-sm focus:outline-none focus:ring-2 dark:border-gray-600 dark:bg-gray-900 dark:text-white ${inputRing}`}
            value={pct}
            onChange={(e) => onPctChange(e.target.value)}
            aria-label={`${label} percentage`}
          />
          <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-base font-semibold text-gray-400">
            %
          </span>
        </div>
      </label>

      <div className="w-full rounded-xl border border-gray-200/80 bg-white px-4 py-4 dark:border-gray-700 dark:bg-gray-900/60">
        <p className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
          Amount
        </p>
        <p className="mt-1 break-words text-3xl font-bold tabular-nums tracking-tight text-gray-900 xl:text-4xl dark:text-white">
          {formatInr(amount)}
        </p>
      </div>
    </div>
  </div>
);

const GrievanceResolvePage: React.FC = () => {
  const { grievanceId } = useParams<{ grievanceId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [detail, setDetail] = useState<GrievanceDetailPayload | null>(null);

  const [vendorPct, setVendorPct] = useState('');
  const [userPct, setUserPct] = useState('');
  const [vedikaPct, setVedikaPct] = useState('');
  const [resolutionNote, setResolutionNote] = useState('');

  const applySharePct = useCallback(
    (edited: ShareKey, raw: string) => {
      const next = applySharePctChange(edited, raw, {
        vendor: vendorPct,
        user: userPct,
        vedika: vedikaPct,
      });
      setVendorPct(next.vendor);
      setUserPct(next.user);
      setVedikaPct(next.vedika);
    },
    [vendorPct, userPct, vedikaPct],
  );

  const goBack = useCallback(() => {
    navigate('/grievances');
  }, [navigate]);

  const load = useCallback(async () => {
    if (!grievanceId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetchGrievanceById(grievanceId);
      if (!res.success || !res.data) throw new Error('Failed to load grievance');
      setDetail(res.data);
      const g = res.data.grievance;
      setVendorPct(g.vendorSharePct != null ? String(g.vendorSharePct) : '');
      setUserPct(g.userSharePct != null ? String(g.userSharePct) : '');
      setVedikaPct(g.vedikaSharePct != null ? String(g.vedikaSharePct) : '');
      setResolutionNote(g.resolutionNote || '');
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Load failed');
      setDetail(null);
    } finally {
      setLoading(false);
    }
  }, [grievanceId]);

  useEffect(() => {
    if (!grievanceId) {
      goBack();
      return;
    }
    void load();
  }, [grievanceId, load, goBack]);

  const g = detail?.grievance;
  const gross = g ? Number(g.totalBookingAmount) : 0;
  const vPctNum = parseFloat(vendorPct) || 0;
  const uPctNum = parseFloat(userPct) || 0;
  const kPctNum = parseFloat(vedikaPct) || 0;
  const pctSum = round2(vPctNum + uPctNum + kPctNum);
  const previewVendor = gross > 0 ? round2((gross * vPctNum) / 100) : 0;
  const previewUser = gross > 0 ? round2((gross * uPctNum) / 100) : 0;
  const previewVedika = gross > 0 ? round2((gross * kPctNum) / 100) : 0;
  const previewTotalAmt = round2(previewVendor + previewUser + previewVedika);
  const pctValid = Math.abs(pctSum - 100) < 0.01;
  const isClosed = g?.status === 'RESOLVED' || g?.status === 'CLOSED';
  const snapshot = detail?.serviceSnapshot ?? null;
  const vendorId = snapshot?.vendorId || g?.vendorId || detail?.vendor?.vendorId || '';
  const refundMissing =
    isClosed &&
    Number(g?.refundAmount) > 0 &&
    (g?.serviceType === 'DOCTOR' || g?.serviceType === 'AMBULANCE') &&
    !detail?.refundInitiated;
  const serviceDetailHref =
    g?.serviceType === 'AMBULANCE' && vendorId
      ? ambulanceBookingPath(vendorId, g.serviceRefId)
      : g?.serviceType === 'MEDICINE' && vendorId
        ? medicineOrderPath(vendorId, g.serviceRefId)
        : null;

  const handleResolve = async () => {
    if (!g || !user?.id) return;
    if (!resolutionNote.trim()) {
      toast.error('Resolution note is required.');
      return;
    }
    if (Math.abs(pctSum - 100) > 0.01) {
      toast.error('Vendor, user, and Vedika shares must total 100%.');
      return;
    }
    setSaving(true);
    try {
      const res = await resolveGrievance(g.grievanceId, {
        vendorSharePct: vPctNum,
        userSharePct: uPctNum,
        vedikaSharePct: kPctNum,
        resolutionNote: resolutionNote.trim(),
        adminId: user.id,
      });
      if (!res.success) {
        throw new Error(res.message || res.warning || 'Resolve failed');
      }
      if (res.warning) {
        toast.success(res.message || 'Grievance resolved.', { duration: 4000 });
        toast(res.warning, { icon: '⚠️', duration: 6000 });
      } else {
        toast.success(res.message || 'Grievance resolved successfully.');
      }
      navigate('/grievances');
    } catch (e: unknown) {
      toast.error(resolveErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  const handleRetryRefund = async () => {
    if (!g) return;
    setRetrying(true);
    try {
      const res = await retryGrievanceRefund(g.grievanceId);
      if (!res.success) {
        throw new Error(res.message || res.error || 'Retry refund failed');
      }
      toast.success(res.message || 'Refund initiated.');
      await load();
    } catch (e: unknown) {
      toast.error(resolveErrorMessage(e));
    } finally {
      setRetrying(false);
    }
  };

  return (
    <div className="mx-auto w-full max-w-[88rem] space-y-4 px-2 pb-8 sm:px-0">
      <Toaster position="top-right" />
      <button
        type="button"
        onClick={goBack}
        className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to grievances
      </button>

      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
        <div className="flex flex-col gap-3 border-b border-gray-200 px-4 py-4 dark:border-gray-700 sm:flex-row sm:items-center sm:justify-between sm:px-6">
          <div className="min-w-0">
            <h1 className="text-xl font-semibold text-gray-900 dark:text-white">Resolve grievance</h1>
            {!loading && g && (
              <p className="mt-0.5 truncate font-mono text-xs text-gray-500 dark:text-gray-400">
                {g.grievanceNumber}
              </p>
            )}
          </div>
          <div className="flex shrink-0 flex-wrap items-center gap-2">
            {!loading && g && (
              <>
                <Link
                  to={maOrderLedgerPath(g.serviceRefId, g.serviceType)}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-800 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-100 dark:hover:bg-gray-700"
                >
                  <Wallet className="h-4 w-4 shrink-0" />
                  Order ledger
                </Link>
                <Link
                  to={userProfilePath(g.userId)}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-800 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-100 dark:hover:bg-gray-700"
                >
                  <User className="h-4 w-4 shrink-0" />
                  User profile
                </Link>
                {serviceDetailHref && (
                  <Link
                    to={serviceDetailHref}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-800 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-100 dark:hover:bg-gray-700"
                  >
                    <Truck className="h-4 w-4 shrink-0" />
                    {g.serviceType === 'MEDICINE' ? 'Order' : 'Booking'}
                    <ExternalLink className="h-3.5 w-3.5 shrink-0 opacity-70" />
                  </Link>
                )}
              </>
            )}
          </div>
        </div>

        {error && (
          <div className="mx-4 mt-4 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800 dark:border-red-900/50 dark:bg-red-950/30 dark:text-red-200 sm:mx-6">
            <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        <div className="flex flex-col lg:flex-row">
          <aside className="shrink-0 border-b border-gray-200 p-4 dark:border-gray-700 sm:p-6 lg:w-[min(100%,380px)] lg:border-b-0 lg:border-r">
            {loading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-primary-500" />
              </div>
            ) : g ? (
              <GrievanceSidebar g={g} user={detail?.user} vendor={detail?.vendor} />
            ) : (
              <p className="text-sm text-gray-500">Grievance not found.</p>
            )}
          </aside>

          <main className="min-w-0 flex-1 space-y-6 p-4 sm:p-6">
            {loading ? (
              <div className="flex justify-center py-16">
                <Loader2 className="h-8 w-8 animate-spin text-primary-500" />
              </div>
            ) : g ? (
              <>
                <section>
                  <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    Grievance
                  </h2>
                  <p className="text-base font-semibold text-gray-900 dark:text-white">{g.title}</p>
                  {g.description ? (
                    <p className="mt-2 whitespace-pre-wrap rounded-lg border bg-gray-50 p-3 text-sm text-gray-700 dark:border-gray-700 dark:bg-gray-900/50 dark:text-gray-300">
                      {g.description}
                    </p>
                  ) : (
                    <p className="mt-2 text-sm text-gray-500">No description provided.</p>
                  )}
                  <GrievanceAttachmentChips attachments={g.attachments} className="mt-3" />
                </section>

                <section>
                  <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    Linked {snapshot?.label || 'service'}
                  </h2>
                  <GrievanceServiceSnapshotPanel snapshot={snapshot} />
                </section>

                {grievanceId && (
                  <section>
                    <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                      Policies
                    </h2>
                    <GrievancePolicyLinks grievanceId={grievanceId} />
                  </section>
                )}

                {isClosed && (
                  <p className="text-sm text-amber-700 dark:text-amber-300">
                    This grievance is already {g.status}.
                  </p>
                )}

                {refundMissing && (
                  <div className="flex flex-col gap-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-200 sm:flex-row sm:items-center sm:justify-between">
                    <p>
                      Razorpay refund of {formatInr(g.refundAmount)} was not created when this case
                      was resolved. Retry to initiate it now.
                    </p>
                    <button
                      type="button"
                      disabled={retrying}
                      onClick={() => void handleRetryRefund()}
                      className="inline-flex shrink-0 items-center justify-center gap-2 rounded-lg bg-amber-700 px-4 py-2 text-sm font-semibold text-white hover:bg-amber-800 disabled:opacity-50"
                    >
                      {retrying ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <IndianRupee className="h-4 w-4" />
                      )}
                      Retry refund
                    </button>
                  </div>
                )}
              </>
            ) : null}
          </main>
        </div>

        {!loading && g && !isClosed ? (
          <div className="border-t-2 border-emerald-500/25 bg-gradient-to-b from-emerald-50/60 to-gray-50/30 dark:border-emerald-800/40 dark:from-emerald-950/20 dark:to-gray-900/20">
            <section className="mx-auto w-full max-w-none space-y-6 px-4 py-6 sm:px-6 lg:px-8">
              <div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Resolution</h2>
                <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                  Split the gross booking amount across vendor, user refund, and Vedika (must total 100%).
                </p>
              </div>

              <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-900/50 sm:p-6">
                      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                        <div className="flex items-start gap-3">
                          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-primary-100 text-primary-700 dark:bg-primary-900/50 dark:text-primary-300">
                            <IndianRupee className="h-6 w-6" />
                          </div>
                          <div>
                            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                              Gross booking amount
                            </p>
                            <p className="mt-0.5 text-3xl font-bold tabular-nums tracking-tight text-gray-900 dark:text-white sm:text-4xl">
                              {formatInr(gross)}
                            </p>
                            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                              Amounts below update as you change each share %
                            </p>
                          </div>
                        </div>
                        <div
                          className={`inline-flex flex-col items-end gap-1 rounded-lg px-3 py-2 text-right ${
                            pctValid
                              ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200'
                              : 'bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-200'
                          }`}
                        >
                          <span className="text-xs font-medium uppercase tracking-wide opacity-80">
                            Share total
                          </span>
                          <span className="text-xl font-bold tabular-nums">
                            {pctSum}%
                            {pctValid ? (
                              <CheckCircle2 className="ml-1 inline h-5 w-5 align-text-bottom" aria-hidden />
                            ) : null}
                          </span>
                          {!pctValid && (
                            <span className="text-xs font-medium">Must equal 100%</span>
                          )}
                        </div>
                      </div>

                      <div className="mt-5 grid grid-cols-1 gap-4 border-t border-gray-100 pt-5 dark:border-gray-700 sm:grid-cols-2 lg:grid-cols-4">
                        <div className="text-center sm:text-left">
                          <p className="text-xs text-gray-500 dark:text-gray-400">Allocated (preview)</p>
                          <p className="text-lg font-semibold tabular-nums text-gray-900 dark:text-white">
                            {formatInr(previewTotalAmt)}
                          </p>
                        </div>
                        <div className="text-center sm:text-left">
                          <p className="text-xs text-gray-500 dark:text-gray-400">Vendor + User + Vedika</p>
                          <p className="text-sm tabular-nums text-gray-700 dark:text-gray-300">
                            {formatInr(previewVendor)} + {formatInr(previewUser)} +{' '}
                            {formatInr(previewVedika)}
                          </p>
                        </div>
                        <div className="text-center sm:text-left">
                          <p className="text-xs text-gray-500 dark:text-gray-400">Remaining</p>
                          <p
                            className={`text-lg font-semibold tabular-nums ${
                              gross > 0 && Math.abs(gross - previewTotalAmt) < 0.02
                                ? 'text-emerald-600 dark:text-emerald-400'
                                : 'text-amber-700 dark:text-amber-300'
                            }`}
                          >
                            {formatInr(round2(Math.max(0, gross - previewTotalAmt)))}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3">
                      <ShareAllocateCard
                        label="Vendor"
                        sublabel="Vendor share of gross"
                        pct={vendorPct}
                        onPctChange={(v) => applySharePct('vendor', v)}
                        amount={previewVendor}
                        icon={Building2}
                        border="border-slate-200 dark:border-slate-600"
                        bg="bg-slate-50 dark:bg-slate-900/40"
                        accent="text-slate-700 dark:text-slate-200"
                        inputRing="focus:ring-slate-400/50"
                      />
                      <ShareAllocateCard
                        label="User refund"
                        sublabel="Refund to customer"
                        pct={userPct}
                        onPctChange={(v) => applySharePct('user', v)}
                        amount={previewUser}
                        icon={User}
                        border="border-emerald-200 dark:border-emerald-800"
                        bg="bg-emerald-50 dark:bg-emerald-950/30"
                        accent="text-emerald-800 dark:text-emerald-200"
                        inputRing="focus:ring-emerald-500/50"
                      />
                      <ShareAllocateCard
                        label="Vedika"
                        sublabel="Platform share"
                        pct={vedikaPct}
                        onPctChange={(v) => applySharePct('vedika', v)}
                        amount={previewVedika}
                        icon={IndianRupee}
                        border="border-primary-200 dark:border-primary-800"
                        bg="bg-primary-50 dark:bg-primary-950/30"
                        accent="text-primary-800 dark:text-primary-200"
                        inputRing="focus:ring-primary-500/50"
                      />
                    </div>

              <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-900/50 sm:p-5">
                <label className="block">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-200">
                    Resolution note <span className="text-red-500">*</span>
                  </span>
                  <textarea
                    rows={4}
                    placeholder="Describe how this grievance was resolved and why these shares were chosen…"
                    className="mt-2 w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm text-gray-900 shadow-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 dark:border-gray-600 dark:bg-gray-900 dark:text-white"
                    value={resolutionNote}
                    onChange={(e) => setResolutionNote(e.target.value)}
                  />
                </label>
              </div>

              <div className="flex flex-col gap-3 border-t border-emerald-200/80 pt-5 dark:border-emerald-900/50 sm:flex-row sm:items-center sm:justify-between">
                <p className="max-w-2xl text-xs text-gray-500 dark:text-gray-400">
                  Confirming will record shares and close this grievance. Refund processing may run
                  separately based on user share.
                </p>
                <button
                  type="button"
                  disabled={saving || !pctValid}
                  onClick={() => void handleResolve()}
                  className="inline-flex w-full shrink-0 items-center justify-center gap-2 rounded-lg bg-emerald-600 px-8 py-3 text-sm font-semibold text-white shadow-sm hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto"
                >
                  {saving ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Resolving…
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className="h-4 w-4" />
                      Confirm resolve
                    </>
                  )}
                </button>
              </div>
            </section>
          </div>
        ) : null}
      </div>
    </div>
  );
};

const GrievanceSidebar: React.FC<{
  g: GrievanceListRow;
  user: GrievanceDetailPayload['user'];
  vendor: GrievanceDetailPayload['vendor'];
}> = ({ g, user, vendor }) => (
  <div className="grid grid-cols-1 gap-3 text-sm">
    <div>
      <span className="text-gray-500 dark:text-gray-400">Status</span>
      <p className="font-medium text-gray-900 dark:text-white">{g.status}</p>
    </div>
    <div>
      <span className="text-gray-500 dark:text-gray-400">Category</span>
      <p className="font-medium text-gray-900 dark:text-white">{g.category}</p>
    </div>
    <div>
      <span className="text-gray-500 dark:text-gray-400">Priority</span>
      <p className="font-medium text-gray-900 dark:text-white">{g.priority}</p>
    </div>
    <div>
      <span className="text-gray-500 dark:text-gray-400">Service</span>
      <p className="font-medium text-gray-900 dark:text-white">
        {g.serviceType} · <span className="font-mono text-xs">{g.serviceRefId}</span>
      </p>
    </div>
    <div>
      <span className="text-gray-500 dark:text-gray-400">Gross amount</span>
      <p className="font-semibold tabular-nums text-gray-900 dark:text-white">
        {formatInr(g.totalBookingAmount)}
      </p>
    </div>
    <div>
      <span className="text-gray-500 dark:text-gray-400">User</span>
      <p className="font-medium text-gray-900 dark:text-white">
        {user?.name || g.userName || g.userId}
      </p>
      {user?.phone_number && <p className="text-xs text-gray-500">{user.phone_number}</p>}
    </div>
    {vendor && (
      <div>
        <span className="text-gray-500 dark:text-gray-400">Vendor</span>
        <p className="break-all font-medium text-gray-900 dark:text-white">
          {vendor.email || vendor.vendorId}
        </p>
      </div>
    )}
    <div>
      <span className="text-gray-500 dark:text-gray-400">Raised</span>
      <p className="text-gray-900 dark:text-white">{new Date(g.createdAt).toLocaleString()}</p>
    </div>
  </div>
);

export default GrievanceResolvePage;
